import React from "react";
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Divider,
} from "@material-ui/core";

const Section = ({ title, children }) => (
  <Box mb={4}>
    <Typography variant="h5" color="primary" gutterBottom>
      {title}
    </Typography>
    <Divider />
    <Typography variant="body1" style={{ marginTop: 10 }}>
      {children}
    </Typography>
  </Box>
);

export default function MontessoriKeys() {
  return (
   <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px', fontFamily: 'Segoe UI, sans-serif', backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
  <h1 style={{ textAlign: 'center', color: '#0f3b62' }}>About Key in Montessori</h1>
<br></br>
  <section style={{ marginBottom: '30px' }}>
    <p>In Montessori education, <strong style={{ color: '#2e86de' }}>"Presented"</strong> refers to the structured introduction of a new material or concept by the teacher (also known as a "directress") to a child. This presentation is a key element in guiding a child's self-directed learning, ensuring they understand how to use the materials and explore them independently.</p>
  </section>

  <section style={{ marginBottom: '30px' }}>
    <p>In Montessori education, <strong style={{ color: '#2e86de' }}>"works independently"</strong> signifies a child's ability to engage in learning activities without direct, constant supervision or instruction from an adult. It emphasizes self-directed learning and exploration, allowing children to choose activities, manage their time, and develop problem-solving skills at their own pace. This fosters self-motivation, concentration, and a deeper understanding of concepts through hands-on experience.</p>
  </section>

  <section style={{ marginBottom: '30px' }}>
    <p>In Montessori education, the phrase <strong style={{ color: '#2e86de' }}>"works with assistance"</strong> is often used in progress reports or observation notes to describe a child’s level of independence with a specific activity or material.</p>
    <h3 style={{ color: '#444', marginTop: '20px',
      marginBlockStart:'20px'
    }}>What “Works with Assistance” Means in Montessori:</h3>
    <ul>
      <li><strong>Partial Independence:</strong> The child can engage with the material or activity, but still needs help — either verbal prompts, physical guidance, or emotional support — to complete it correctly.</li>
      <li><strong>Learning in Progress:</strong> The child is in the learning or practicing phase, not yet mastering the work independently but showing interest and capability with some support.</li>
      <li><strong>Guided Development:</strong> The Montessori guide (teacher) may provide:
        <ul style={{ marginLeft: '20px' }}>
          <li>Reminders about steps in the sequence</li>
          <li>Help with setup or cleanup</li>
          <li>Modelling the correct use of the material</li>
          <li>Encouragement to maintain focus</li>
        </ul>
      </li>
    </ul>
  </section>

  <section>
    <h3 style={{ color: '#444' }}>Examples:</h3>
    <ul>
      <li>A child uses the Pink Tower, but the guide reminds them of the correct size order — this would be “works with assistance.”</li>
      <li>A child is learning to write letters with the Sandpaper Letters and needs help tracing or remembering sounds — “works with assistance.”</li>

    </ul>
  </section>
  <section style={{ marginTop: '25px' }}>
    <h3 style={{ color: '#0f3b62' }}>Importance:</h3>
    <ul>
      <li>Montessori values independence, so “works with assistance” signals that the child is on the path to mastery.</li>
      <li>It also helps educators track progress and adjust the level of support as needed.</li>
    </ul>
  </section>

  <section style={{ marginTop: '30px' }}>
    <p>In Montessori education, “<strong>Sandpaper Letters</strong>” are a key material used to introduce children to the alphabet through a <strong>multi-sensory approach</strong>—tactile, visual, and auditory.</p>
    <p>Here’s a detailed understanding of what Sandpaper Letters are and how they’re used:</p>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h2 style={{ color: '#0f3b62' }}>What Are Sandpaper Letters?</h2>
    <p>Sandpaper Letters are <strong>individual wooden or cardboard cards</strong> with <strong>lowercase or uppercase letters</strong> made of <strong>fine sandpaper</strong>. The child <strong>traces the letter with their fingers</strong> while saying the corresponding <strong>phonetic sound</strong>.</p>
    <ul>
      <li><strong>Consonants</strong> are typically on <strong>pink or blue cards</strong>.</li>
      <li><strong>Vowels</strong> are often on <strong>blue or red cards</strong> (colours may vary by school).</li>
      <li><strong>Cursive or print</strong> styles may be used depending on the Montessori philosophy in the school.</li>
    </ul>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h2 style={{ color: '#0f3b62' }}>Purpose of Sandpaper Letters</h2>
    <ol style={{ paddingLeft: '20px' }}>
      <li><strong>Phonemic Awareness:</strong> Children learn the <strong>sounds</strong> (not names) of letters.</li>
      <li><strong>Muscle Memory:</strong> Tracing develops the <strong>motor memory</strong> needed for writing.</li>
      <li><strong>Tactile Engagement:</strong> Touching the rough surface enhances <strong style={{ color: '#a75c9c' }}>kinesthetic learning</strong>.</li>
      <li><strong>Letter Recognition:</strong> Children become familiar with <strong>letter shapes</strong> and sounds simultaneously.</li>
    </ol>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h2 style={{ color: '#0f3b62' }}>How Are They Used in Practice?</h2>
    <ol>
      <li>
        <strong>Three-Period Lesson</strong> (a Montessori teaching technique):
        <ul style={{ marginLeft: '20px' }}>
          <li><strong>First Period:</strong> “This is <em>m</em>.” (Direct teaching)</li>
          <li><strong>Second Period:</strong> “Can you show me <em>m</em>?” (Recognition)</li>
                    <li><strong>Third  Period:</strong> “What is this <em>m</em>?” (Recall)</li>

        </ul>
      </li>
    </ol>
  </section>
  <section>
    <ol>
      <li>
        <strong>Phonetic Sounds First:</strong> Children are taught the <strong>sound</strong> each letter makes, not the name (e.g., “mmmm” instead of “em”).
      </li>
      <li>
        <strong>Traced with Fingers:</strong> Children trace the letters <strong>left to right</strong>, following the proper stroke order.
      </li>
    </ol>
  </section>

  <hr style={{ margin: '30px 0', borderTop: '1px solid #ddd' }} />

  <section>
    <h3 style={{ color: '#0f3b62' }}>Connection to Reading & Writing</h3>
    <ul>
      <li>These letters precede writing <strong>with a pencil</strong>.</li>
      <li>They are used in conjunction with the <strong>Movable Alphabet</strong> to start building words.</li>
      <li>Children begin <strong>phonetic spelling</strong> before formal reading and writing.</li>
    </ul>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h3 style={{ color: '#0f3b62' }}>Why It’s Effective</h3>
    <p>
      Maria Montessori designed this method based on the <strong>sensitive period for language</strong> (ages 2.5–6). The use of touch, movement, and sound taps into <strong>natural learning tendencies</strong> during this stage.
    </p>
    <p>
      If you're working with or observing a Montessori classroom, you’ll likely see Sandpaper Letters used around <strong>age 3–4</strong>, typically before or alongside the child’s first writing attempts.
    </p>
    <p>
      Let me know if you'd like an activity guide or printable template suggestions!
    </p>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h3 style={{ color: '#0f3b62' }}>The Movable Alphabet</h3>
    <p>
      The <strong>Movable Alphabet</strong> is a core material in the Montessori language curriculum, used to help children develop early literacy skills. It is typically introduced after a child has learned a number of phonetic sounds through the <strong>Sandpaper Letters</strong> and is ready to start forming words.
    </p>
  </section>

  <section>
    <h4 style={{ marginTop: '15px' }}>What is the Movable Alphabet?</h4>
    <ul>
      <li>A set of letters (usually lowercase) made of wood or plastic.</li>
      <li>Consonants are often <strong>blue</strong>, and vowels are <strong>red</strong> (though this can vary).</li>
      <li>The letters are stored in a compartmentalized box, each in its own section.</li>
    </ul>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h3 style={{ color: '#0f3b62' }}>Purpose and Educational Goals</h3>
    <p><strong>The Movable Alphabet helps children:</strong></p>
    <ul>
      <li>Understand that <strong>spoken words can be represented in writing</strong>.</li>
      <li>
        Begin to compose words and later sentences <strong>without needing to write by hand</strong>, which can be physically difficult at an early age.
      </li>
      <li>
        Practice <strong>phonemic awareness</strong>—breaking down words into individual sounds.
      </li>
      <li>
        Learn <strong>spelling patterns</strong> and <strong>sound-symbol relationships</strong>.
      </li>
      <li>
        Build a bridge between <strong>spoken language and writing</strong>.
      </li>
    </ul>
  </section>
   <section>
    <h3 style={{ color: '#0f3b62' }}>How It’s Used</h3>
    <ol>
      <li>
        <strong>Word Building:</strong>
        <ul>
          <li>A child hears a word (e.g., “cat”) and selects the corresponding letters: <strong>c – a – t</strong>.</li>
          <li>They arrange the letters on a mat or table to form the word.</li>
        </ul>
      </li>
      <li>
        <strong>Storytelling:</strong>
        <p>As skills grow, children can build phrases or stories with the alphabet, expressing ideas before they’re able to write fluently.</p>
      </li>
      <li>
        <strong>Extensions:</strong>
        <ul>
          <li>Work with pictures (e.g., matching a picture of a dog to the word “dog”).</li>
          <li>Incorporate grammar (e.g., color-coded alphabets to highlight parts of speech).</li>
          <li>Use for <strong>phonogram practice</strong> (e.g., <em>sh, th, ch</em>, etc.).</li>
        </ul>
      </li>
    </ol>
  </section>

  <hr style={{ margin: '30px 0', borderTop: '1px solid #ddd' }} />

  <section>
    <h3 style={{ color: '#0f3b62' }}>Developmental Benefits</h3>
    <ul>
      <li>Encourages <strong>creativity and confidence</strong> in writing.</li>
      <li>Reinforces <strong>sound-symbol correspondence</strong>.</li>
      <li>Prepares children for <strong>writing and reading</strong>.</li>
      <li>Allows <strong>self-correction and exploration</strong> without the pressure of handwriting.</li>
    </ul>
  </section>

  <section style={{ marginTop: '30px' }}>
    <h3 style={{ color: '#0f3b62' }}>When It’s Introduced</h3>
    <ul>
      <li>Around <strong>3.5 to 4 years old</strong>, depending on the child’s readiness.</li>
      <li>After the child has a solid foundation of <strong>letter sounds</strong> from the Sandpaper Letters.</li>
    </ul>
  </section>
</div>

  );
}
